// hooks/useTransfertsCanadaCmr.ts
import { useState, useEffect, useCallback } from "react";
import { bankService, BankTransfertItem } from "../../services/bankService";
import useToken from "../../../../hooks/auth/useToken";

interface UseTransfertsCanadaCmrProps {
  skip?: number;
  limit?: number;
  status?: string;
  autoFetch?: boolean;
  direction?: string;
}

export default function useTransfertsCanadaCmr({
  skip = 0,
  limit = 50,
  status = undefined,
  autoFetch = true,
  direction = "canada-cmr-banque",
}: UseTransfertsCanadaCmrProps = {}) {
  const [bankTransfertsItems, setBankTransfertsItems] = useState<BankTransfertItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);

  const { token } = useToken();

  const fetchTransferts = useCallback(async () => {
    if (!token) {
      setError("Token non trouvé");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await bankService.getTransfertsCanadaCmr(
        token,
        {
          skip,
          limit,
          status,
        },
        direction
      );

      setBankTransfertsItems(response);
      setTotal(response.length);
      setHasMore(response.length === limit);
    } catch (err: any) {
      setError(err.message || "Erreur lors du chargement des transferts");
      console.error("Erreur fetchTransferts:", err);
    } finally {
      setLoading(false);
    }
  }, [token, skip, limit, status]);

  const refetch = useCallback(() => {
    fetchTransferts();
  }, [fetchTransferts]);

  const filterByStatus = useCallback(
    (statusFilter: string) => {
      return bankTransfertsItems.filter((item) => item.transfert.status === statusFilter);
    },
    [bankTransfertsItems]
  );

  // Stats avec CAD et XAF
  const stats = {
    totalAmountCAD: bankTransfertsItems.reduce((sum, item) => sum + item.transfert.sending_amount, 0),
    totalAmountXAF: bankTransfertsItems.reduce((sum, item) => sum + item.transfert.receiving_amount, 0),
    totalFeesCAD: bankTransfertsItems.reduce((sum, item) => sum + item.transfert.fee, 0),
    pendingCount: bankTransfertsItems.filter((item) => item.transfert.status === "PENDING").length,
    completedCount: bankTransfertsItems.filter((item) => item.transfert.status === "SUCCEEDED").length,
    averageRate:
      bankTransfertsItems.length > 0
        ? bankTransfertsItems.reduce((sum, item) => sum + item.transfert.conversion_rate, 0) /
          bankTransfertsItems.length
        : 0,
  };

  useEffect(() => {
    if (autoFetch && token) {
      fetchTransferts();
    }
  }, [autoFetch, token, fetchTransferts]);

  return {
    bankTransfertsItems,
    loading,
    error,
    total,
    hasMore,
    stats,
    refetch,
    filterByStatus,
    isEmpty: bankTransfertsItems.length === 0 && !loading,
    isPending: loading,
  };
}
